/**
 * Shared date/time formatting helpers.
 *
 * Convention across the app:
 *  - dates are rendered as `d/m/Y`      e.g. `22/07/2026`
 *  - times are rendered as `H:i:s`      e.g. `15:24:08`
 *  - full timestamps as `d/m/Y H:i:s`   e.g. `22/07/2026 15:24:08`
 */

type DateInput = Date | string | number | null | undefined;

const pad = (value: number): string => String(value).padStart(2, '0');

const toDate = (value: DateInput): Date | null => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Formats a date as `d/m/Y`, e.g. `22/07/2026`. Returns '' for missing/invalid input. */
export const formatDate = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return '';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

/** Formats the time part as `H:i:s`, e.g. `15:24:08`. Returns '' for missing/invalid input. */
export const formatTimeOfDay = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return '';
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/** Formats a full timestamp as `d/m/Y H:i:s`, e.g. `22/07/2026 15:24:08`. Returns '' for missing/invalid input. */
export const formatDateTime = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return '';
  return `${formatDate(date)} ${formatTimeOfDay(date)}`;
};

/**
 * Normalizes a clock-time string to `H:i` with minutes always present.
 * e.g. `11` -> `11:00`, `8:5` -> `08:05`, `08:00` -> `08:00`.
 * Non-numeric input is returned untouched.
 */
export const formatClockTime = (value: string | null | undefined): string => {
  if (!value) return '';
  const [rawHours, rawMinutes = '0'] = value.split(':');
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return value;
  return `${pad(hours)}:${pad(minutes)}`;
};
